import { USER } from '../actions/user';
import store from '../store';
import { isLocal, apiUrl } from '../config';

const call = (method, path, headers, data) => {
  const params = {
    method,
    headers: headers || {},
  };
  params.headers['Content-Type'] = 'application/json';

  if (data) {
    if (data.file) {
      // TODO: change for few files or mixed data
      delete params.headers['Content-Type'];
      /*
       * Setting the Content-Type header manually means it's missing the boundary parameter.
       * Remove that header and allow fetch to generate the full content type.
       * It will look something like this:
       * Content-Type: multipart/form-data;boundary=----WebKitFormBoundaryyrV7KO0BoCBuDbTL
       * Fetch knows which content type header to create based
       * on the FormData object passed in as the request body content.
       */
      const formData = new FormData();
      formData.append('file', data.file);
      params.body = formData;
    } else {
      params.body = JSON.stringify(data);
    }
  }

  const debugPath = isLocal
    ? `${path}${path.includes('?') ? '&' : '?'}${Object.entries({
      XDEBUG_SESSION_START: 'PHPSTORM',
    }).map(([key, value]) => `${key}=${value}`).join('&')}`
    : path;

  return fetch(path[0] === '/' ? apiUrl + debugPath : debugPath, params).then((res) => {
    if (res.status === 401 || res.status === 403) {
      // TODO: just 401 should be not authorized, but server returns 403 sometimes
      store.dispatch({ type: USER.NOT_AUTHORIZED });
      return { error: { msg: res.statusText } };
    }
    return res.json();
  }).catch((error) => {
    window.console.log(error);
    return { error: { msg: 'Network error' } };
  });
};
const get = (path, headers, params) => {
  const arg = params
    ? Object.keys(params).map(key => `${key}=${params[key]}`)
    : [];
  return call('GET', path + (arg.length ? `?${arg.join('&')}` : ''), headers);
};
const post = (path, headers, data) => call('POST', path, headers, data);
const put = (path, headers, data) => call('PUT', path, headers, data);
const del = (path, headers) => call('DELETE', path, headers);
const getToken = () => store.getState().user.token;

/* Menu */
export const getBreadcrumbs = names => post('/management/breadcrumbs', { token: getToken() }, names);
export const getMenuCounters = (token = null) => get('/management/counters', { token: token || getToken() });

/* User */
export const login = (username, password) => post('/management/users/login', {}, { email: username, password });
export const register = fields => post('/management/users/register', {}, fields);
export const unregister = () => del('/management/users/unregister', { token: getToken() });
export const logout = () => get('/management/users/logout', { token: getToken() });
export const resetPassword = email => post('/management/users/password/forgotten', {}, { email });
export const getMe = () => get('/management/users/me', { token: getToken() });
export const updateMe = fields => put('/management/users/me', { token: getToken() }, fields);
export const getUserSettings = () => get('/management/users/config', { token: getToken() });
export const updateUserSettings = fields => put('/management/users/config', { token: getToken() }, fields);
export const uploadUserImage = (file, userId) => post(`/management/storage?entity=user&entity_id=${userId}`, { token: getToken() }, { file });
export const removeUserImage = (storageId, userId) =>
  del(
    `/management/storage/${storageId}?entity=user&entity_id=${userId}`,
    { token: getToken() },
    { storageId, userId },
  );
export const changeLanguage = locale => put(`/management/users/lang/${locale}`, { token: getToken() });
export const getConfirmation = confirmationToken => get(`/management/users/confirmation/${confirmationToken}`, {}, {});

/* Trainings */
export const loadTrainings = params => get('/learning/trainings', { token: getToken() }, params);
export const loadTrainingResults = id => get(`/learning/trainings/${id}/results`, { token: getToken() });
export const getStartConfirmation = id => get(`/learning/trainings/${id}/start`, { token: getToken() });
export const saveStartConfirmation = (id, cycleId) => post(`/learning/trainings/${id}/start`, { token: getToken() }, { cycleId });
export const getFinishConfirmation = id => get(`/learning/trainings/${id}/finish`, { token: getToken() });
export const saveFinishConfirmation = (id, cycleId) => post(`/learning/trainings/${id}/finish`, { token: getToken() }, { cycleId });
export const setInterviewed = trainingId => get(`/learning/trainings/${trainingId}/interview`, { token: getToken() });

/* Lessons */
export const loadLessons = (id, params) => get(`/learning/trainings/${id}/lessons`, { token: getToken() }, params);
export const loadLessonById = id => get(`/learning/lessons/${id}`, { token: getToken() });
export const loadLessonResults = id => get(`/learning/lessons/${id}/results`, { token: getToken() });
export const completeLesson = id => put(`/learning/lessons/${id}/complete`, { token: getToken() });

/* Contents */
export const loadContents = (id, params) => get(`/learning/lessons/${id}/contents`, { token: getToken() }, params);
export const loadContent = id => get(`/learning/lessons/contents/${id}`, { token: getToken() });
export const loadContentsTraining = (id, params) => get(`/learning/trainings/${id}/contents`, { token: getToken() }, params);
export const loadContentTraining = id => get(`/learning/lessons/contents/${id}`, { token: getToken() });

/* Passing */
export const loadNextQuestion = lessonId => get(`/learning/lessons/${lessonId}/passing`, { token: getToken() });
export const saveReply = (lessonId, fields) => post(`/learning/lessons/${lessonId}/passing`, { token: getToken() }, fields);

export const getInterviewed = interviewedToken => get(`/management/trainings/interviewed/${interviewedToken}`, {}, {});
export const getSigned = signedToken => get(`/management/trainings/signed/${signedToken}`, {}, {});

/* Info */
export const loadInfo = page => get(`/learning/${page}`, { token: getToken() });

/* Certificates */
export const loadCertificates = params => get('/learning/certificates', { token: getToken() }, params);

/* Notes */
export const loadNotes = (entity, id, params) => get(id === 'all' ? '/learning/notes' : `/learning/${entity}/${id}/notes`, { token: getToken() }, params);
export const addNote = (id, text) => post(`/learning/lessons/${id}/notes`, { token: getToken() }, { text });
export const updateNote = (id, text) => put(`/learning/notes/${id}`, { token: getToken() }, { text });
export const deleteNote = id => del(`/learning/notes/${id}`, { token: getToken() });

/* Pictures */
export const loadPictures = (entity, id, params) => get(id === 'all' ? '/learning/images' : `/learning/${entity}/${id}/images`, { token: getToken() }, params);
export const addPicture = (id, file) => post(`/management/storage?entity=myimages&entity_id=${id}`, { token: getToken() }, { file });
export const deletePicture = id => del(`/learning/images/${id}`, { token: getToken() });

/* Company colors */
export const getCompanyColors = () => get('/management/config/colors', {}, {});
export const getCompanyRegParams = () => get('/management/config/registration', {}, {});

export const getLanguages = () => get(`/management/config/languages`, {});

export const getTranslations = (locale) => get(`/management/config/translations/${locale}`, {}, {});


/* Chats */
export const loadChats = params => get('/learning/chats', { token: getToken() }, params);
export const startChat = fields => post('/learning/chats', { token: getToken() }, fields);
export const startSupportChat = () => get('/learning/support', { token: getToken() });
export const userSuggestions = searchString => get(`/learning/chats/users?search=${searchString}`, { token: getToken() });
export const uploadChatImage = file => post('/management/storage?entity=chat', { token: getToken() }, { file });
// export const deleteChatImage = id => del(`/learning/chats/images/${id}`, { token: getToken() });
export const loadMessages = (id, params) => get(`/learning/chats/${id}`, { token: getToken() }, params);
export const sendMessage = (id, params) => post(`/learning/chats/${id}`, { token: getToken() }, params);
export const uploadMessageImage = file => post('/management/storage?entity=message', { token: getToken() }, { file });



// WEBPACK FOOTER //
// ./src/utils/Api.js